import { useQuery } from "@/lib/query";
import { useMemo, useState } from "react";

import { BookingCard, BookingsLoadError, EmptyBookings } from "@/components/doctor/booking-card";
import { RequireDoctor } from "@/components/doctor/require-doctor";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useClassifiedBookings } from "@/hooks/doctor/use-classified-bookings";
import { bookingsQuery } from "@/lib/api/doctorApi";
import { compareBookings, type BookingView } from "@/lib/booking-view";
import { useAuth } from "@/contexts/AuthContext";
import { usePageMeta } from "@/lib/page-meta";

export function BookingsRoute() {
  usePageMeta({
    title: "Bookings — HealNova Doctor Portal",
    description: "Every patient appointment booked with you on HealNova, filterable by status.",
  });
  return <RequireDoctor>{() => <BookingsPage />}</RequireDoctor>;
}

type Filter = BookingView | "all";

const FILTERS: { value: Filter; label: string; empty: string }[] = [
  {
    value: "booked",
    label: "Booked",
    empty: "Nothing open right now. New bookings appear here as patients schedule with you.",
  },
  {
    value: "completed",
    label: "Completed",
    empty: "No consultations completed yet. A call both you and the patient join lands here.",
  },
  {
    value: "missed",
    label: "Missed",
    empty: "Nothing missed. Appointments move here when their slot ends without a call.",
  },
  { value: "cancelled", label: "Cancelled", empty: "No appointments have been cancelled." },
  { value: "all", label: "All", empty: "Nothing here yet." },
];

function BookingsPage() {
  const { uid } = useAuth();
  const query = useQuery(bookingsQuery(uid));
  const { items, now } = useClassifiedBookings(query.data);
  const [filter, setFilter] = useState<Filter>("booked");
  const [search, setSearch] = useState("");

  const counts = useMemo(() => {
    const tally: Record<Filter, number> = {
      booked: 0,
      completed: 0,
      missed: 0,
      cancelled: 0,
      all: items.length,
    };
    for (const item of items) tally[item.view] += 1;
    return tally;
  }, [items]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items
      .filter((item) => filter === "all" || item.view === filter)
      .filter((item) => !term || item.booking.patientName.toLowerCase().includes(term))
      .sort((a, b) => compareBookings(a.booking, b.booking, filter));
  }, [items, filter, search]);

  const active = FILTERS.find((entry) => entry.value === filter) ?? FILTERS[0]!;

  return (
    <div className="mx-auto w-full max-w-5xl space-y-7">
      <header>
        <p className="text-xs font-semibold tracking-[0.18em] text-rose uppercase">Appointments</p>
        <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
          Bookings
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Every consultation patients have scheduled with you, grouped by status.
        </p>
      </header>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={filter} onValueChange={(value) => setFilter(value as Filter)}>
          <TabsList>
            {FILTERS.map((entry) => (
              <TabsTrigger key={entry.value} value={entry.value}>
                {entry.label}
                {/* A failed load has no counts — showing 0 would assert the
                    buckets are empty when they are merely unknown. */}
                {!query.isPending && !query.isError && (
                  <span className="ml-1.5 text-xs tabular-nums opacity-70">
                    {counts[entry.value]}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search patient name"
          aria-label="Search bookings by patient name"
          className="h-10 w-full sm:w-64"
        />
      </div>

      {query.isPending ? (
        <div className="space-y-3">
          {[0, 1, 2, 3].map((key) => (
            <div key={key} className="h-28 animate-pulse rounded-2xl bg-muted" />
          ))}
        </div>
      ) : query.isError ? (
        // Before the empty branch: an unloaded list is unknown, not empty.
        <BookingsLoadError
          error={query.error}
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        />
      ) : visible.length === 0 ? (
        <EmptyBookings message={search ? `No bookings match “${search}”.` : active.empty} />
      ) : (
        <ul className="space-y-3">
          {visible.map((item) => (
            <BookingCard key={item.booking.id} booking={item.booking} view={item.view} now={now} />
          ))}
        </ul>
      )}
    </div>
  );
}
